import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { env } from '@/utils/env';
import { toast } from 'sonner';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | null;
  google: typeof google | null;
  reloadMapsApi: () => void;
  requestLoad: () => void;
  geocoder: google.maps.Geocoder | null;
  /**
   * @deprecated Always `null`. The legacy `AutocompleteService` is no longer
   * constructed; suggestions go through `@/utils/address/placeSuggestions`.
   * The field remains so existing consumers and test doubles keep type-checking.
   */
  autocompleteService: null;
}

const GoogleMapsContext = createContext<GoogleMapsContextType | undefined>(undefined);

// Track global loading state
let globalLoadingPromise: Promise<void> | null = null;

export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [google, setGoogle] = useState<typeof window.google | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [requested, setRequested] = useState(false);
  const scriptElementRef = useRef<HTMLScriptElement | null>(null);
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  // Use a more robust loading approach
  const loadGoogleMapsScript = useCallback(() => {
    // If the API is already loaded and available globally, return immediately
    if (window.google && window.google.maps && window.google.maps.places) {
//       ');
      setIsLoaded(true);
      setGoogle(window.google);
      setGeocoder(new window.google.maps.Geocoder());
      return Promise.resolve();
    }
    
    // Reuse existing global promise if available
    if (globalLoadingPromise) {
      return globalLoadingPromise;
    }
    
    
    // Reset states for a new load attempt
    setIsLoaded(false);
    setLoadError(null);
    
    // Create a single global loading promise
    globalLoadingPromise = new Promise<void>((resolve, reject) => {
      // Create a unique callback name
      const callbackName = `initGoogleMapsCallback_${Math.random().toString(36).substring(2, 10)}`;
      
      // Define the callback function
      window[callbackName] = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          setLoadError(null);
          setGoogle(window.google);
          setGeocoder(new window.google.maps.Geocoder());
          resolve();
        } else {
          const error = new Error('Google Maps API callback executed but APIs not available');
          setLoadError(error);
          setIsLoaded(false);
          reject(error);
        }
        // Clean up the callback
        delete window[callbackName];
        // Reset the global promise after it completes
        setTimeout(() => {
          if (globalLoadingPromise === globalLoadingPromise) {
            globalLoadingPromise = null;
          }
        }, 100);
      };
      
      try {
        // Remove any existing script to avoid duplicates
        if (scriptElementRef.current) {
          try {
            document.head.removeChild(scriptElementRef.current);
            scriptElementRef.current = null;
          } catch (e) {
          }
        }
        
        // Create and add the script element
        const script = document.createElement('script');
        // See the note in address-utils.ts: loading=async is the documented
        // companion to a callback load, and silences Google's warning.
        script.src = `https://maps.googleapis.com/maps/api/js?key=${env.GOOGLE_MAPS_API_KEY}&libraries=places,geometry,drawing&loading=async&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        
        script.onerror = (error) => {
          const errorMsg = error instanceof Event ? 'Script loading error' : error.toString();
          const loadError = new Error(errorMsg);
          setLoadError(loadError);
          setIsLoaded(false);
          reject(loadError);
          globalLoadingPromise = null;
        };
        
        // Add the script to the document
        document.head.appendChild(script);
        scriptElementRef.current = script;
        
        // Set a timeout in case callback isn't called
        const timeoutId = setTimeout(() => {
          if (!window.google || !window.google.maps || !window.google.maps.places) {
            const timeoutError = new Error('Google Maps API loading timed out');
            setLoadError(timeoutError);
            setIsLoaded(false);
            reject(timeoutError);
            globalLoadingPromise = null;
          }
        }, 15000);
        
        return () => clearTimeout(timeoutId);
      } catch (error) {
        const typedError = error instanceof Error ? error : new Error('Unknown error loading Google Maps API');
        setLoadError(typedError);
        setIsLoaded(false);
        reject(typedError);
        globalLoadingPromise = null;
      }
    });
    
    return globalLoadingPromise;
  }, []);

  const reloadMapsApi = useCallback(() => {
    toast.info('Reloading Google Maps API...');
    
    // Reset the global loading promise
    globalLoadingPromise = null;
    
    // Properly clean up the existing script
    if (scriptElementRef.current) {
      try {
        document.head.removeChild(scriptElementRef.current);
        scriptElementRef.current = null;
      } catch (e) {
      }
    }
    
    // Cleanup existing Google Maps references
    if (window.google && window.google.maps) {
      // @ts-ignore - force reload
      window.google.maps = undefined;
    }
    
    setLoadAttempt(prev => prev + 1);
  }, []);

  const requestLoad = useCallback(() => {
    setRequested(true);
  }, []);

  useEffect(() => {
    if (!requested) return;
    loadGoogleMapsScript().catch(err => {
      console.error('Failed to load Google Maps:', err);
      EnhancedErrorLogger.getInstance().logQuickError(err instanceof Error ? err : new Error(String(err)), 'GoogleMaps', { context: 'load script' });
    });
  }, [requested, loadAttempt, loadGoogleMapsScript]);

  const value = {
    isLoaded,
    loadError,
    google,
    reloadMapsApi,
    requestLoad,
    geocoder,
    autocompleteService: null as null,
  };

  return <GoogleMapsContext.Provider value={value}>{children}</GoogleMapsContext.Provider>;
};

export const useGoogleMaps = (): GoogleMapsContextType => {
  const context = useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  useEffect(() => {
    context.requestLoad();
  }, [context.requestLoad]);
  return context;
};
